import musicPack from './app/core/curriculum/school-exercise-packs/music-audio-30.json';
import { SpeechService } from './app/core/speech/speech.service';

interface ToneStep {
  readonly frequencyHz: number;
  readonly durationMs: number;
}

interface ToneSequenceStimulus {
  readonly kind: 'tone-sequence';
  readonly waveform: 'sine';
  readonly gain: number;
  readonly gapMs: number;
  readonly tones: readonly ToneStep[];
}

interface MusicItem {
  readonly id: string;
  readonly prompt: string;
  readonly audioStimulus: ToneSequenceStimulus;
}

const MUSIC_PANEL_ID = 'learning-garden-music-audio';
const FADE_SECONDS = 0.02;
const musicItems = musicPack.families.flatMap((family) => family.items as readonly MusicItem[]);
const itemByPrompt = new Map(musicItems.map((item) => [item.prompt, item] as const));

let audioContext: AudioContext | null = null;
let activeOscillators: OscillatorNode[] = [];
let activePrompt: string | null = null;
let playbackToken = 0;
let speechService: SpeechService | null = null;
let syncScheduled = false;

function stopTones(): void {
  playbackToken += 1;
  for (const oscillator of activeOscillators) {
    try {
      oscillator.stop();
    } catch {
      // A scheduled oscillator may already have stopped.
    }
    oscillator.disconnect();
  }
  activeOscillators = [];
}

function setAnswersLocked(card: HTMLElement, locked: boolean): void {
  const answers = card.querySelector<HTMLElement>('.instruction-answers');
  if (!answers) return;
  answers.classList.toggle('music-audio-locked', locked);
  if (locked) answers.setAttribute('inert', '');
  else answers.removeAttribute('inert');
}

function removePanel(card?: HTMLElement | null): void {
  document.getElementById(MUSIC_PANEL_ID)?.remove();
  if (card) {
    card.classList.remove('music-audio-active');
    setAnswersLocked(card, false);
  }
  activePrompt = null;
  stopTones();
}

function panelFor(card: HTMLElement, item: MusicItem): HTMLElement {
  let panel = document.getElementById(MUSIC_PANEL_ID);
  if (panel) return panel;

  panel = document.createElement('section');
  panel.id = MUSIC_PANEL_ID;
  panel.className = 'music-audio-panel';
  panel.setAttribute('aria-label', 'Stimolo sonoro dell’esercizio');
  panel.innerHTML = `
    <button class="music-audio-play" type="button" aria-label="Ascolta i due suoni">
      <span class="music-audio-play-icon" aria-hidden="true">▶️</span>
      <span>Ascolta i suoni</span>
    </button>
    <p class="music-audio-status" aria-live="polite">Ascolta prima di scegliere.</p>
  `;

  const instructionBoard = card.querySelector('.instruction-board');
  if (instructionBoard) instructionBoard.before(panel);
  else card.querySelector('.instruction-answers')?.before(panel);

  const button = panel.querySelector<HTMLButtonElement>('.music-audio-play')!;
  button.addEventListener('click', () => void playItem(card, item, panel!));
  return panel;
}

async function playItem(card: HTMLElement, item: MusicItem, panel: HTMLElement): Promise<void> {
  const button = panel.querySelector<HTMLButtonElement>('.music-audio-play');
  const status = panel.querySelector<HTMLElement>('.music-audio-status');
  if (!button || !status) return;

  speechService?.cancel();
  stopTones();
  const token = ++playbackToken;

  const AudioContextClass = window.AudioContext;
  if (!AudioContextClass) {
    status.textContent = 'Questo dispositivo non può riprodurre lo stimolo sonoro.';
    return;
  }

  audioContext ??= new AudioContextClass();
  try {
    if (audioContext.state === 'suspended') await audioContext.resume();
  } catch {
    status.textContent = 'Tocca di nuovo per consentire l’audio.';
    return;
  }

  button.disabled = true;
  button.classList.add('playing');
  status.textContent = 'Ascolta…';
  setAnswersLocked(card, true);

  const stimulus = item.audioStimulus;
  let startAt = audioContext.currentTime + 0.06;
  let totalMs = 60;
  const scheduled: OscillatorNode[] = [];

  for (const tone of stimulus.tones) {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.type = stimulus.waveform;
    oscillator.frequency.setValueAtTime(tone.frequencyHz, startAt);

    const endAt = startAt + tone.durationMs / 1000;
    gainNode.gain.setValueAtTime(0, startAt);
    gainNode.gain.linearRampToValueAtTime(stimulus.gain, startAt + FADE_SECONDS);
    gainNode.gain.setValueAtTime(stimulus.gain, Math.max(startAt + FADE_SECONDS, endAt - FADE_SECONDS));
    gainNode.gain.linearRampToValueAtTime(0, endAt);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.start(startAt);
    oscillator.stop(endAt + 0.01);
    scheduled.push(oscillator);

    startAt = endAt + stimulus.gapMs / 1000;
    totalMs += tone.durationMs + stimulus.gapMs;
  }

  activeOscillators = scheduled;
  window.setTimeout(() => {
    if (token !== playbackToken || activePrompt !== item.prompt) return;
    activeOscillators = [];
    button.disabled = false;
    button.classList.remove('playing');
    status.textContent = 'Ora scegli la risposta. Puoi riascoltare quando vuoi.';
    setAnswersLocked(card, false);
  }, totalMs + 80);
}

function syncMusicSurface(): void {
  syncScheduled = false;
  const card = document.querySelector<HTMLElement>('.exercise-card');
  if (!card) {
    removePanel();
    return;
  }

  const prompt = card.querySelector<HTMLElement>('h1.sr-only')?.textContent?.trim() ?? '';
  const item = itemByPrompt.get(prompt);
  if (!item) {
    if (activePrompt !== null) removePanel(card);
    return;
  }

  if (activePrompt !== prompt) {
    removePanel(card);
    activePrompt = prompt;
    card.classList.add('music-audio-active');
    setAnswersLocked(card, true);
    panelFor(card, item);
  }
}

function scheduleSync(): void {
  if (syncScheduled) return;
  syncScheduled = true;
  window.requestAnimationFrame(syncMusicSurface);
}

export function installMusicAudioController(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const speechPrototype = SpeechService.prototype;
  const originalSpeak = speechPrototype.speak;
  speechPrototype.speak = function musicAwareSpeak(
    this: SpeechService,
    text: string,
    locale = 'it-IT'
  ): void {
    speechService = this;
    originalSpeak.call(this, text, locale);
  };

  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  scheduleSync();
}
